export function toEmojiChar(raw) {
  if (raw === null || raw === undefined) return "";

  // 1. Already a real emoji (non-ASCII, not a number string) → pass through
  //    Quick check: if it contains non-ASCII chars it's likely already an emoji
  if (typeof raw === "string" && /\p{Emoji}/u.test(raw) && !/^\d+$/.test(raw)) {
    return raw;
  }

  // 2. Plain number (decimal code point)
  if (typeof raw === "number") {
    try {
      return String.fromCodePoint(raw);
    } catch {
      return String(raw);
    }
  }

  if (typeof raw === "string") {
    // 3. Decimal numeric string e.g. "128077"
    if (/^\d+$/.test(raw)) {
      try {
        return String.fromCodePoint(parseInt(raw, 10));
      } catch {
        return raw;
      }
    }

    // 4. U+ hex notation e.g. "U+1F44D" or "U+1F1FA-U+1F1F8" (flags)
    if (/^U\+/i.test(raw)) {
      try {
        const codePoints = raw
          .split("-")
          .map((part) => parseInt(part.replace(/^U\+/i, ""), 16));
        return String.fromCodePoint(...codePoints);
      } catch {
        return raw;
      }
    }

    // 5. Raw hex string e.g. "1F44D" or "1F1FA-1F1F8"
    if (/^[0-9A-Fa-f]+([-][0-9A-Fa-f]+)*$/.test(raw)) {
      try {
        const codePoints = raw.split("-").map((part) => parseInt(part, 16));
        // Sanity check: valid Unicode range (0 – 0x10FFFF)
        if (codePoints.every((cp) => cp >= 0 && cp <= 0x10ffff)) {
          return String.fromCodePoint(...codePoints);
        }
      } catch {
        // Fall through to pass-through
      }
    }

    // 6. Shortcode ":thumbsup:" — pass through (requires external library)
    //    If you add emoji-dictionary, swap this for a lookup.
    if (/^:[a-z0-9_+-]+:$/i.test(raw)) {
      console.warn(
        "[toEmojiChar] Shortcode emoji not converted (no lookup library):",
        raw,
      );
      return raw;
    }

    // 7. Anything else → return as-is
    return raw;
  }

  return String(raw);
}

/**
 * Normalize an array of reaction objects so every `emoji` field
 * holds an actual emoji character string, and `users` is always
 * an array of plain strings (safe for comparison regardless of
 * whether the server sent ObjectId objects or plain strings).
 *
 * @param {Array} reactions
 * @returns {Array}
 */
export function normalizeReactions(reactions) {
  if (!Array.isArray(reactions)) return reactions ?? [];

  return reactions.map((r) => ({
    ...r,
    emoji: toEmojiChar(r.emoji),
    // Normalize users to plain strings so optimistic-update comparisons
    // work correctly even when the server returns MongoDB ObjectId objects.
    users: Array.isArray(r.users)
      ? r.users.map((u) =>
          u && typeof u.toString === "function"
            ? u.toString()
            : String(u ?? ""),
        )
      : [],
  }));
}

/**
 * Normalize a CHANNEL message into a consistent UI shape.
 */
export function normalizeChannelMessage(msg) {
  if (!msg) return null;

  // The sender details might be in msg.sender (object) from new aggregation,
  // or msg.senderInfo (object) from other pipelines.
  const info =
    msg.sender && typeof msg.sender === "object"
      ? msg.sender
      : msg.senderInfo || {};

  const senderName =
    info.name ||
    (typeof msg.sender === "string" ? msg.sender : null) ||
    msg.senderName ||
    "Unknown";

  const normalizedReactions = normalizeReactions(msg.reactions);

  return {
    ...msg,
    reactions: normalizedReactions,
    senderId:
      info._id ||
      msg.senderId ||
      (typeof msg.sender === "string" ? null : msg.sender?._id) ||
      null,
    senderName,
    senderAvatar: info.profilePhoto || msg.senderAvatar || null,
    senderUsername: info.username || msg.senderUsername || null,
    senderInfo: {
      _id:
        info._id ||
        msg.senderId ||
        (typeof msg.sender === "string" ? null : msg.sender?._id) ||
        null,
      name: senderName,
      profilePhoto: info.profilePhoto || msg.senderAvatar || null,
      username: info.username || msg.senderUsername || null,
    },
    // ── Thread indicator fields ───────────────────────────────────────────
    // Map server field names → UI field names used by ThreadIndicator.
    // Preserve any value already set (e.g. from a real-time socket update)
    // so a socket patch is never overwritten by a stale undefined.
    replyCount: msg.replyCount ?? msg.threadReplyCount ?? 0,
    lastReplyAt: msg.lastReplyAt ?? msg.lastThreadReplyAt ?? null,
  };
}

/**
 * Normalize a DIRECT MESSAGE into a consistent UI shape.
 */
export function normalizeDMMessage(msg, currentUserId) {
  if (!msg) return null;

  // Direct messages might have the populated user in 'sender' (object)
  const info =
    msg.sender && typeof msg.sender === "object"
      ? msg.sender
      : msg.senderInfo || {};

  const senderName =
    info.name ||
    (typeof msg.sender === "string" ? msg.sender : null) ||
    msg.senderName ||
    "Unknown";

  const senderId =
    info._id ||
    msg.senderId ||
    (typeof msg.sender === "string" ? null : msg.sender?._id) ||
    null;

  const normalizedReactions = normalizeReactions(msg.reactions);

  // return {
  //   ...msg,
  //   reactions: normalizedReactions,
  //   senderId,
  //   senderName,
  //   senderAvatar: info.profilePhoto || msg.senderAvatar || null,
  //   senderUsername: info.username || msg.senderUsername || null,
  //   senderInfo: {
  //     _id: senderId,
  //     name: senderName,
  //     profilePhoto: info.profilePhoto || msg.senderAvatar || null,
  //     username: info.username || msg.senderUsername || null,
  //   },
  //   isMine:
  //     currentUserId && senderId
  //       ? senderId.toString() === currentUserId.toString() ||
  //         (typeof msg.sender === "string" && msg.sender === currentUserId)
  //       : false,
  // };

  //=============================================
  return {
    ...msg,
    reactions: normalizedReactions,
    senderId,
    senderName,
    senderAvatar: info.profilePhoto || msg.senderAvatar || null,
    senderUsername: info.username || msg.senderUsername || null,
    senderInfo: {
      _id: senderId,
      name: senderName,
      profilePhoto: info.profilePhoto || msg.senderAvatar || null,
      username: info.username || msg.senderUsername || null,
    },
    isMine:
      currentUserId && senderId
        ? senderId.toString() === currentUserId.toString() ||
          (typeof msg.sender === "string" && msg.sender === currentUserId)
        : false,
    replyCount: msg.threadReplyCount ?? msg.replyCount ?? 0,
    lastReplyAt: msg.lastThreadReplyAt ?? msg.lastReplyAt ?? null,
    lastReplyUser: msg.lastThreadReplyUser ?? null,
    threadParticipants: msg.threadParticipants ?? [],
    hasThreadReplies: msg.hasThreadReplies ?? msg.threadReplyCount > 0 ?? false,
  };
  //=============================================
}

/**
 * Normalize an array of channel messages.
 */
export function normalizeChannelMessages(messages = []) {
  return messages.map(normalizeChannelMessage).filter(Boolean);
}

/**
 * Normalize an array of DM messages.
 */
export function normalizeDMMessages(messages = [], currentUserId) {
  return messages
    .map((m) => normalizeDMMessage(m, currentUserId))
    .filter(Boolean);
}
